import { useEffect, useMemo, useState } from "react";
import { GridActionsCellItem } from "@mui/x-data-grid";
import {
  Add,
  CreditCard,
  LocalAtm,
  NearMe,
  Visibility,
} from "@mui/icons-material";
import {
  getTerminatedMeterBillings,
  saveMeterBillingNote,
  saveMeterBillingPayment,
} from "../services/meterbilling.service";
import Grid from "@mui/material/Grid";
import {
  Alert,
  Box,
  Button,
  Divider,
  InputLabel,
  Modal,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import "dayjs/locale/en-gb";
import {isNullUndefined, tryParseFloat} from "../util/util";
import ReceivePayment from "../components/modals/receivePayment";
import {
  issueResponseMessage,
  issueWarnMessage,
} from "../actions/message";
import {
  InnerModal,
  MainContainer,
  TopBarContainer,
} from "../constants/component.constants";
import { ResponsiveRow } from "../constants/layout.constants";
import Permit from "../util/permit";
import MuiDataGrid from "../components/modules/MuiDataGrid";
import DebitNote from "../components/modals/debitNote";
import ViewMeterBilling from "../components/modules/meterBilling/viewMeterBilling";
import CreditNote from "../components/modals/creditNote";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  height: "auto",
  bgcolor: "background.paper",
  borderRadius: "10px",
  boxShadow: "4px 4px 12px #333",
  p: 4,
};

export default function TerminatedMeterBilling() {
  const [searchReference, setSearchReference] = useState("");
  const [customer, setCustomer] = useState("");
  const [isLoading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [ViewDiv, setViewDiv] = useState(false);
  const [PaymentModal, setPaymentModal] = useState(false);
  const handlePaymentClose = () => setPaymentModal(false);
  const [creditModal, setCreditModal] = useState(false);
  const [NoteModal, setNoteModal] = useState(false);
  const [ChargeModal, setChargeModal] = useState(false);
  const [note, setNote] = useState("");
  const [selectedMeterBilling, setSelectedMeterBilling] = useState(null);
  const [voidItem, setVoidItem] = useState(null);

  const handleCreditClose = () => {
    setCreditModal(false);
    fetchData();
  };
  const fetchData = (controller = null) => {
    setLoading(true);
    getTerminatedMeterBillings(searchReference, customer, {signal: controller?.signal}).then((meterBillings) => {
      setRows(meterBillings)
    }).finally(() => setLoading(false));
  }

  const handleChargeClose = () => {
    setChargeModal(false);
    setLoading(true)
    fetchData();
  };

  useEffect(() => {
    const controller = new AbortController();
    const timerId = setTimeout(() => {
      // Only start loading if the data fetching is truly new
      fetchData(controller);
    }, 500);

    return () => {
      // Cleanup function
      clearTimeout(timerId);
      controller.abort();
    };

  }, [searchReference, customer]);

  function onTextChange(searchText) {
    const id = searchText.target.id;
    const value = searchText.target.value;
    if (id === "searchCode") {
      setSearchReference(value);
    }
    if (id === "searchCustomer") {
      setCustomer(value);
    }
    if (id === "note") {
      setNote(value);
    }
  }

  function savePayment(data) {
    data.meterBillingId = selectedMeterBilling.id;
    saveMeterBillingPayment(data)
      .then((response) => {
        issueResponseMessage(response);
        if (!isNullUndefined(response.meterBilling)) {
          handlePaymentClose();
          setLoading(true);
          fetchData()
        }
      })
      .catch((error) => {
        console.error("Error saving payment:", error);
        issueWarnMessage("Failed to save payment. Please try again.");
        setLoading(false);
      })
  }

  function saveNote() {
    let data = {
      meterBillingId: selectedMeterBilling.id,
      note: note,
    };
    saveMeterBillingNote(data).then((response) => {
      issueResponseMessage(response);
      setNoteModal(false);
    });
  }

  function handleViewClick(selectedMeterBilling) {
    setSelectedMeterBilling(selectedMeterBilling);
    setViewDiv(true);
  }

  function handlePaymentClick(selectedMeterBilling) {
    setSelectedMeterBilling(selectedMeterBilling);
    setPaymentModal(true);
  }

  function handleCreditClick(selectedMeterBilling) {
    setSelectedMeterBilling(selectedMeterBilling);
    setCreditModal(true);
  }

  function handleChargeClick(selectedMeterBilling) {
    setSelectedMeterBilling(selectedMeterBilling);
    setChargeModal(true);
  }

  function handleNoteClick(selectedMeterBilling) {
    setSelectedMeterBilling(selectedMeterBilling);
    setNoteModal(true);
  }
  
  function handleVoidClick(item) {
    if (item.type === "Charge" || item.type === "Invoice") {
      setCreditModal(true);
      //set the id of the transaction being voided
      setVoidItem(item);
    } else if (item.type === "Receipt" || item.type === "Credit") {
      setChargeModal(true);
      setVoidItem(item);
    }
  }

  const columns = useMemo(() => [
    {
      headerName: "Billing Code",
      field: "billingCode",
      minWidth: 140,
      flex: 1.4,
    },
    { headerName: "Meter(s)", field: "meters", minWidth: 220, flex: 2.2 },
    {
      headerName: "Billing Frequency",
      field: "billingFrequency",
      minWidth: 190,
      flex: 1.9,
    },
    {
      headerName: "Account Holder",
      field: "contracteeName",
      minWidth: 390,
      flex: 3.9,
    },
    {
      headerName: "Balance",
      field: "meterBillingBalance",
      minWidth: 170,
      flex: 1.7,
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 100,
      type: "actions",
      getActions: (params) => {
        let bal = tryParseFloat(params.row.meterBillingBalance, 0);
        let arr = [];
        arr.push(
          <GridActionsCellItem
            icon={<Visibility />}
            label="View"
            onClick={() => handleViewClick(params.row)}
            showInMenu
          />
        );

        arr.push(
          <GridActionsCellItem
            icon={<Add />}
            label="Add Note"
            onClick={() => handleNoteClick(params.row)}
            showInMenu
          />
        );

        if (bal > 0) {
          arr.push(
            <GridActionsCellItem
              icon={<LocalAtm />}
              label="Receive Payment"
              onClick={() => handlePaymentClick(params.row)}
              showInMenu
            />
          );
        }

        arr.push(
          <GridActionsCellItem
            icon={<CreditCard />}
            label="Issue Credit"
            onClick={() => handleCreditClick(params.row)}
            showInMenu
          />
        );

        arr.push(
          <GridActionsCellItem
            icon={<NearMe />}
            label="Issue Charge"
            onClick={() => handleChargeClick(params.row)}
            showInMenu
          />
        );
        return arr;
      },
    },
  ]);

  return (
    <Permit
      roles="BLDIR,BLDMG,CS"
      noPermittedServiceMessage={{
        title: "Access Denied",
        body: (
          <>
            Your current role is not permitted to view this page. <br /> Please
            contact your system admin if you feel this is an error.
          </>
        ),
      }}
    >
      <MainContainer>
        {!ViewDiv && (
          <TopBarContainer
            container
            sx={{
              width: { xs: "100%", md: "100%" },
              boxShadow: "4px 4px 12px #aaa",
            }}
          >
            <ResponsiveRow
                item
                sx={{
                  width: {xs: "100%", lg: "30%"},
                  justifyContent: {xs: "center", lg: "flex-start"},
                  height: "auto",
                }}
            >
              <Typography
                  sx={{textAlign: "left"}}
                  id="terminatedMeterBilling"
                  variant="h6"
                  component="h6"
              >
                Terminated Meter Billing
              </Typography>
            </ResponsiveRow>
            <ResponsiveRow
              item
              sx={{
                flexWrap: "wrap",
                justifyContent: "flex-start",
                width: { xs: "100%", lg: "70%" },
                padding: 0,
                textAlign: "start",
              }}
            >
              <TextField
                id="searchCode"
                label="Search billing code"
                sx={{
                  width: { xs: "100%", md: "45%" },
                  margin: "5px",
                }}
                onChange={(searchText) => onTextChange(searchText)}
              />
              <TextField
                id="searchCustomer"
                label="Search Customer"
                sx={{
                  width: { xs: "100%", md: "45%" },
                  margin: "5px",
                }}
                onChange={(searchText) => onTextChange(searchText)}
              />
            </ResponsiveRow>
          </TopBarContainer>
        )}

        <br />

        {!ViewDiv && (
          <MuiDataGrid
            dataGridColumns={columns}
            dataGridRows={rows}
            serverPagination={false}
            loading={isLoading}
            height="60vh"
            totalRows={rows.length}
          />
        )}

        {ViewDiv && (
          <ViewMeterBilling
            selectedMeterBilling={selectedMeterBilling}
            handleClose={() => setViewDiv(false)}
            voidItem={voidItem}
            handleVoidClick={handleVoidClick}
          />
        )}

        <br />

        {/*RECEIVE PAYMENT MODAL*/}
        {PaymentModal && (
          <ReceivePayment
            showPaymentModal={PaymentModal}
            handlePaymentClose={handlePaymentClose}
            paymentMode={"Meter Billing"}
            contract={selectedMeterBilling}
            savePayment={savePayment}
          />
        )}

        {/*CREATE CREDIT MODAL*/}
        {creditModal && (
          <CreditNote
            open={creditModal}
            onClose={handleCreditClose}
            voidItem={voidItem}
            meterbillingid={selectedMeterBilling && selectedMeterBilling.id}
          />
        )}

        {/*CREATE CHARGE MODAL*/}
        {ChargeModal && (
          <DebitNote
            open={ChargeModal}
            onClose={handleChargeClose}
            meterbillingid={selectedMeterBilling?.id}
          />
        )}

        {/*ADD NOTE MODAL*/}
        <Modal
          open={NoteModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Typography id="modal-modal-title" variant="h3" component="h2">
              Add Note To Meter Billing{" "}
              {selectedMeterBilling && selectedMeterBilling.billingCode} for{" "}
              {selectedMeterBilling && selectedMeterBilling.contracteeName}
            </Typography>
            <Divider />
            <br />

            <Paper
              variant="outlined"
              sx={{ height: "auto", margin: "auto", padding: "10px" }}
            >
              <Alert severity="info">
                Add note to meter billing will log important notes/information
                pertaining this meter billing. All the notes added on this meter
                billing will be displayed chronologically, from newest first to
                oldest. Notes will be limited to 500 words.
              </Alert>
              <br />
              <InnerModal container>
                <Grid item xs={12}>
                  <InputLabel>Note (500)</InputLabel>
                  <TextField
                    sx={{ width: "100%" }}
                    id={"note"}
                    name={"note"}
                    value={note}
                    isrequired
                    onChange={(event) => onTextChange(event)}
                  />
                </Grid>
              </InnerModal>
              <br />
            </Paper>
            <br />

            {/*FOOTER WITH BUTTONS*/}
            <Paper
              variant="outlined"
              sx={{ height: "auto", margin: "auto", padding: "10px" }}
            >
              <Button
                variant="contained"
                type="submit"
                onClick={saveNote}
                sx={{ backgroundColor: "#037171" }}
              >
                {" "}
                Save Note{" "}
              </Button>
              <Button
                variant="contained"
                type="submit"
                onClick={() => setNoteModal(false)}
                sx={{ marginLeft: "10px" }}
                color="error"
              >
                {" "}
                Cancel{" "}
              </Button>
            </Paper>
          </Box>
        </Modal>
      </MainContainer>{" "}
    </Permit>
  );
}
